import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { DialogFooter } from '@/components/ui/dialog';
import type { InputType, Question } from '@/lib/api';

const INPUT_TYPES: { value: InputType; label: string; description: string }[] = [
  { value: 'tap', label: 'הקשות (DTMF)', description: 'קלט בהקשות חיוג' },
  { value: 'stt', label: 'דיבור (STT)', description: 'קלט קולי - חייב יחידות במערכת ימות' },
  { value: 'announcement', label: 'השמעה בלבד', description: 'משמיע טקסט וממשיך לשאלה הבאה ללא קלט' }
];

export function QuestionForm({
  initial,
  onSave,
  onCancel
}: {
  initial?: Partial<Question>;
  onSave: (data: Partial<Question>) => Promise<void>;
  onCancel: () => void;
}) {
  const [key, setKey] = useState(initial?.key || '');
  const [label, setLabel] = useState(initial?.label || '');
  const [promptText, setPromptText] = useState(initial?.promptText || '');
  const [inputType, setInputType] = useState<InputType>(initial?.inputType || 'tap');
  const [isRequired, setIsRequired] = useState(initial?.isRequired ?? true);
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [confirmRequired, setConfirmRequired] = useState(initial?.confirmRequired ?? false);
  const [confirmPrompt, setConfirmPrompt] = useState(
    initial?.confirmPrompt || 'הנתון שהוקש הוא {value}, לאישור הקש 1, לתיקון הקש 2'
  );
  const [optionsJson, setOptionsJson] = useState(JSON.stringify(initial?.options || {}, null, 2));

  // tap fields
  const opts = (initial?.options || {}) as Record<string, unknown>;
  const [minDigits, setMinDigits] = useState<string>(String(opts.min_digits ?? '1'));
  const [maxDigits, setMaxDigits] = useState<string>(String(opts.max_digits ?? '12'));
  const [secWait, setSecWait] = useState<string>(String(opts.sec_wait ?? ''));
  const [allowEmpty, setAllowEmpty] = useState<boolean>(Boolean(opts.allow_empty));
  const [digitsAllowed, setDigitsAllowed] = useState<string>(
    Array.isArray(opts.digits_allowed) ? (opts.digits_allowed as string[]).join(',') : ''
  );
  const [blockAsterisk, setBlockAsterisk] = useState<boolean>(Boolean(opts.block_asterisk_key));
  const [blockZero, setBlockZero] = useState<boolean>(Boolean(opts.block_zero_key));
  const [typingPlayback, setTypingPlayback] = useState<string>(
    String(opts.typing_playback_mode ?? 'No')
  );

  // stt fields
  const [lang, setLang] = useState<string>(String(opts.lang ?? 'he'));
  const [maxSeconds, setMaxSeconds] = useState<string>(String(opts.max_seconds ?? '30'));

  const [busy, setBusy] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);

  function buildOptions(): Record<string, unknown> {
    if (advancedMode) {
      try {
        return JSON.parse(optionsJson);
      } catch {
        return {};
      }
    }
    if (inputType === 'announcement') return {};
    if (inputType === 'tap') {
      const o: Record<string, unknown> = {};
      if (minDigits) o.min_digits = Number(minDigits);
      if (maxDigits) o.max_digits = Number(maxDigits);
      if (secWait) o.sec_wait = Number(secWait);
      if (allowEmpty) o.allow_empty = true;
      if (digitsAllowed.trim()) {
        o.digits_allowed = digitsAllowed.split(',').map((s) => s.trim()).filter(Boolean);
      }
      if (blockAsterisk) o.block_asterisk_key = true;
      if (blockZero) o.block_zero_key = true;
      if (typingPlayback && typingPlayback !== 'No') o.typing_playback_mode = typingPlayback;
      return o;
    }
    if (inputType === 'stt') {
      const o: Record<string, unknown> = {};
      if (lang) o.lang = lang;
      if (maxSeconds) o.max_seconds = Number(maxSeconds);
      if (secWait) o.sec_wait = Number(secWait);
      return o;
    }
    return {};
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          const options = buildOptions();
          await onSave({
            key,
            label,
            promptText,
            inputType,
            options,
            isRequired,
            isActive,
            confirmRequired: inputType === 'announcement' ? false : confirmRequired,
            confirmPrompt: confirmRequired ? confirmPrompt : null
          });
        } finally {
          setBusy(false);
        }
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="key">מפתח (אנגלית) *</Label>
          <Input
            id="key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            required
            placeholder="monthly_usage"
            dir="ltr"
            disabled={!!initial?.id}
          />
          <p className="text-xs text-muted-foreground">
            ישמש כשם עמודה בייצוא Excel. אותיות קטנות + מספרים + קו תחתון.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="label">תווית *</Label>
          <Input
            id="label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            placeholder="סך שימוש חודשי"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="promptText">טקסט להשמעה *</Label>
        <Textarea
          id="promptText"
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          required
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label>סוג קלט *</Label>
        <div className="grid grid-cols-3 gap-2">
          {INPUT_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setInputType(t.value)}
              className={`rounded-lg border p-3 text-start text-sm transition-colors ${
                inputType === t.value
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="font-medium">{t.label}</div>
              <div className="mt-1 text-xs text-muted-foreground">{t.description}</div>
            </button>
          ))}
        </div>
      </div>

      {inputType !== 'announcement' && (
        <Tabs value={advancedMode ? 'advanced' : 'simple'} onValueChange={(v) => setAdvancedMode(v === 'advanced')}>
          <TabsList>
            <TabsTrigger value="simple">אפשרויות קלט</TabsTrigger>
            <TabsTrigger value="advanced">מתקדם (JSON)</TabsTrigger>
          </TabsList>
          <TabsContent value="simple" className="space-y-3 pt-3">
            {inputType === 'tap' && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="min_digits">מינימום ספרות</Label>
                    <Input
                      id="min_digits"
                      type="number"
                      value={minDigits}
                      onChange={(e) => setMinDigits(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="max_digits">מקסימום ספרות</Label>
                    <Input
                      id="max_digits"
                      type="number"
                      value={maxDigits}
                      onChange={(e) => setMaxDigits(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="sec_wait">המתנה (שניות)</Label>
                    <Input
                      id="sec_wait"
                      type="number"
                      value={secWait}
                      onChange={(e) => setSecWait(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="digits_allowed">ספרות מותרות (מופרד בפסיקים)</Label>
                  <Input
                    id="digits_allowed"
                    value={digitsAllowed}
                    onChange={(e) => setDigitsAllowed(e.target.value)}
                    placeholder="1,2,3"
                    dir="ltr"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={allowEmpty} onCheckedChange={setAllowEmpty} id="allow_empty" />
                    <Label htmlFor="allow_empty">לאפשר ריק</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={blockAsterisk} onCheckedChange={setBlockAsterisk} id="block_ast" />
                    <Label htmlFor="block_ast">חסום כוכבית</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={blockZero} onCheckedChange={setBlockZero} id="block_zero" />
                    <Label htmlFor="block_zero">חסום אפס מהתחלה</Label>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="typing_playback">מצב השמעת ההקשה</Label>
                  <Select value={typingPlayback} onValueChange={setTypingPlayback}>
                    <SelectTrigger id="typing_playback">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="No">ללא</SelectItem>
                      <SelectItem value="Number">מספר</SelectItem>
                      <SelectItem value="Digits">ספרות</SelectItem>
                      <SelectItem value="HebrewKeyboard">מקלדת עברית</SelectItem>
                      <SelectItem value="EnglishKeyboard">מקלדת אנגלית</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {inputType === 'stt' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="lang">שפה</Label>
                    <Select value={lang} onValueChange={setLang}>
                      <SelectTrigger id="lang">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="he">עברית</SelectItem>
                        <SelectItem value="en-US">English (US)</SelectItem>
                        <SelectItem value="ar">ערבית</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="max_seconds">מקסימום שניות</Label>
                    <Input
                      id="max_seconds"
                      type="number"
                      value={maxSeconds}
                      onChange={(e) => setMaxSeconds(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </TabsContent>
          <TabsContent value="advanced" className="pt-3">
            <Label htmlFor="opts_json">JSON של אופציות</Label>
            <Textarea
              id="opts_json"
              value={optionsJson}
              onChange={(e) => setOptionsJson(e.target.value)}
              rows={8}
              className="font-mono text-xs"
              dir="ltr"
            />
          </TabsContent>
        </Tabs>
      )}

      {inputType !== 'announcement' && (
        <div className="space-y-3 rounded-md border p-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={confirmRequired}
              onCheckedChange={setConfirmRequired}
              id="confirm_required"
            />
            <Label htmlFor="confirm_required">לבקש אישור על הקלט</Label>
          </div>
          {confirmRequired && (
            <div className="space-y-1">
              <Label htmlFor="confirm_prompt">טקסט אישור (משתמש ב-{'{value}'})</Label>
              <Textarea
                id="confirm_prompt"
                value={confirmPrompt}
                onChange={(e) => setConfirmPrompt(e.target.value)}
                rows={2}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        {inputType !== 'announcement' && (
          <div className="flex items-center gap-2">
            <Switch checked={isRequired} onCheckedChange={setIsRequired} id="is_required" />
            <Label htmlFor="is_required">חובה</Label>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Switch checked={isActive} onCheckedChange={setIsActive} id="is_active" />
          <Label htmlFor="is_active">פעיל</Label>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          ביטול
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? 'שומר...' : 'שמור'}
        </Button>
      </DialogFooter>
    </form>
  );
}
